/**
 * Module Home (mode classique) — refonte alpha (item 8).
 *
 * Structure (remplace la vue agrégée HiFi.5) :
 *
 *   1. `EditorialHero` : badge foyer + date-line italique + « Bonjour,
 *      *Camille*. ».
 *   2. **InfoStrip 3 boxes** (remplace le KpiStrip chiffres) :
 *      ① prochain **événement familial** (event sans `who` → concerne
 *        toute la famille : anniversaire, sortie…), ② **repas du jour**
 *        (midi/soir), ③ **2-3 derniers messages**. Chaque box navigue
 *        vers sa vue (planning / meals / messages).
 *   3. Section « Aujourd'hui · La journée de chacun » : **une carte par
 *      membre** avec SES événements du jour + SES tâches ouvertes
 *      (cochables directement). Remplace le planning partagé.
 *   4. `AiSuggestion` (bienveillant, toujours visible — ton positif).
 *   5. Section « Cette semaine » + repas planifiés (inchangé).
 *
 * Les filtres purs (`eventsForMemberToday`, `tasksForMemberOpen`,
 * `nextEventForMember`, `nextFamilyEvent`, `todayMealsOf`,
 * `lastMessagesOf`) sont exportés et couverts par les tests unit
 * (`home-filters.test.ts`).
 *
 * Toutes les chaînes UI passent par `t(key)`. Les données viennent
 * exclusivement de `getCurrentHome().data` (CLAUDE.md §2.2).
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import styles from './View.module.css';
import { getLang, t } from '../../i18n/index.js';
import { getCurrentHome, toggleTask } from '../../stores/homes.js';
import { compareIso, isAfterToday, todayIso } from '../../lib/date.js';
import { navigate } from '../../lib/router.js';
import type {
  ClassicHomeData,
  FamilyEvent,
  FamilyMessage,
  FamilyTask,
  Meal,
  Member,
} from '@nestor/shared-types';
import { editorialHero } from '../../ui/Hero.js';
import { sectionHead } from '../../ui/SectionHead.js';
import { listRow } from '../../ui/ListRow.js';
import {
  conditionIconName,
  conditionLabelKey,
  enableWeather,
  getWeatherState,
  refreshWeather,
} from './weather.js';

// ── Filtres purs (conservés depuis Phase 1, utilisés en tests) ───────

export function eventsForMemberToday(
  events: readonly FamilyEvent[],
  memberId: string,
): FamilyEvent[] {
  const today = todayIso();
  return events.filter((e) => e.who === memberId && e.date === today);
}

export function tasksForMemberOpen(tasks: readonly FamilyTask[], memberId: string): FamilyTask[] {
  return tasks.filter((tk) => tk.who === memberId && !tk.done);
}

export function nextEventForMember(
  events: readonly FamilyEvent[],
  memberId: string,
): FamilyEvent | null {
  const upcoming = events
    .filter((e) => e.who === memberId && isAfterToday(e.date))
    .sort((a, b) => compareIso(a.date, b.date));
  return upcoming[0] ?? null;
}

// ── Filtres purs — refonte alpha (item 8) ────────────────────────────

/**
 * Prochain **événement familial** : un event sans `who` concerne toute
 * la famille (anniversaire programmé, sortie familiale…). On prend le
 * plus proche dont la date est aujourd'hui ou après.
 */
export function nextFamilyEvent(events: readonly FamilyEvent[]): FamilyEvent | null {
  const today = todayIso();
  const upcoming = events
    .filter((e) => !e.who && (e.date === today || isAfterToday(e.date)))
    .sort((a, b) => compareIso(a.date, b.date));
  return upcoming[0] ?? null;
}

/** Repas du jour (midi / soir) depuis la grille hebdo. */
export function todayMealsOf(meals: readonly Meal[]): { lunch?: string; dinner?: string } {
  const todayMeal = meals.find((m) => m.today);
  if (!todayMeal) return {};
  const lunch = todayMeal.lunch?.trim();
  const dinner = todayMeal.dinner?.trim() || todayMeal.m.trim();
  return { ...(lunch ? { lunch } : {}), ...(dinner ? { dinner } : {}) };
}

/** Les `n` derniers messages, du plus récent au plus ancien. */
export function lastMessagesOf(messages: readonly FamilyMessage[], n = 3): FamilyMessage[] {
  return [...messages].sort((a, b) => b.at - a.at).slice(0, n);
}

// ── Helpers d'affichage spécifiques à la home ──────────────────────────

/**
 * Choisit un « prénom d'accent » pour le hero. Sans session on salue le
 * premier **parent** du foyer — jamais un ado/enfant par accident de
 * position dans la liste (audit A1 : « Bonjour, Emma. » quand l'ado
 * était le seul membre restant). Sans parent : pas d'accent, le titre
 * « Bonjour. » seul fonctionne.
 */
function accentNameFor(home: { name: string; data: ClassicHomeData }): string | undefined {
  const parent = home.data.members.find((m) => m.role === 'parent');
  if (parent) return parent.name.split(/\s+/)[0];
  return undefined;
}

/**
 * Date-line localisée façon « mardi 12 mai ». Utilise `Intl.
 * DateTimeFormat` avec la locale courante. Pas hardcodé en FR pour
 * que les 7 langues fonctionnent.
 */
function dateLineToday(): string {
  const lang = getLang();
  try {
    return new Intl.DateTimeFormat(lang, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(new Date());
  } catch {
    return todayIso();
  }
}

/** Date courte localisée (« sam. 12 juil. ») pour la box événement. */
function shortDate(iso: string): string {
  const lang = getLang();
  try {
    return new Intl.DateTimeFormat(lang, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}

// Couleurs d'avatar déterministes (même palette que Settings).
const AVATAR_PALETTE = ['#7A9E8E', '#5B6C8F', '#C49060', '#9B7CB6', '#5B8DB8', '#D27C63'];

function avatarColorFor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length]!;
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]!)
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function firstNameOf(members: readonly Member[], id: string | undefined): string {
  if (!id) return '?';
  const m = members.find((x) => x.id === id);
  return m?.name.split(/\s+/)[0] ?? '?';
}

/**
 * Liste des repas prévus cette semaine — pour la section « Cette
 * semaine ». On filtre les jours qui ont au moins un repas (lunch,
 * dinner ou legacy `m`). Le « jour » s'affiche en kicker court
 * (« lun. », « ce soir » pour today).
 */
interface WeekMealEntry {
  id: string;
  dayLabel: string;
  title: string;
  meta?: string;
}

function weekMeals(data: ClassicHomeData): WeekMealEntry[] {
  return data.meals.flatMap((meal: Meal, idx: number) => {
    const dinner = meal.dinner?.trim() || meal.m.trim();
    const lunch = meal.lunch?.trim();
    const entries: WeekMealEntry[] = [];
    const baseDay = (meal.d ?? '').toLowerCase();
    const todayLabel = meal.today ? t('tonight') : `${baseDay}.`;
    if (dinner) {
      entries.push({
        id: `meal-${idx.toString()}-dinner`,
        dayLabel: todayLabel,
        title: dinner,
        meta: meal.tag || t('home_family'),
      });
    }
    if (lunch) {
      entries.push({
        id: `meal-${idx.toString()}-lunch`,
        dayLabel: `${baseDay}.`,
        title: lunch,
        meta: t('home_family'),
      });
    }
    return entries;
  });
}

// ── Widget météo (hero trailing, façon widget iOS) ────────────────────

/**
 * Carte météo du jour, posée dans le slot `trailing` du hero (haut
 * droite). Sans activation ni cache → bouton « Activer la météo »
 * (déclenche la géoloc au clic, jamais au chargement). Avec données →
 * emoji + température + condition + max/min ; un clic rafraîchit.
 */
function weatherWidget(): TemplateResult {
  const st = getWeatherState();

  // Aucune donnée et pas de chargement en cours : proposer l'activation.
  if (!st.data && st.status !== 'locating' && st.status !== 'loading') {
    return html`<button
      type="button"
      class=${styles.weatherEnable}
      data-testid="home-weather-enable"
      @click=${(): void => {
        enableWeather();
      }}
    >
      <span class=${styles.weatherEnableIcon} aria-hidden="true">${Icon(Icons.cloudSun, 15)}</span>
      <span>${st.status === 'denied' ? t('weather_denied') : t('weather_enable')}</span>
    </button>`;
  }

  // En cours sans cache : petit état de chargement discret.
  if (!st.data) {
    return html`<div class=${styles.weatherCard} data-testid="home-weather-loading">
      <span class=${styles.weatherLoading}>${t('weather_loading')}</span>
    </div>`;
  }

  const w = st.data;
  return html`<button
    type="button"
    class=${styles.weatherCard}
    data-testid="home-weather"
    aria-label=${`${t(conditionLabelKey(w.condition))} · ${w.temp.toString()}°`}
    title=${t('weather_refresh')}
    @click=${(): void => {
      void refreshWeather();
    }}
  >
    <div class=${styles.weatherTop}>
      <span class=${styles.weatherEmoji} aria-hidden="true"
        >${Icon(Icons[conditionIconName(w.condition)], 22)}</span
      >
      <span class=${styles.weatherTemp}>${w.temp}°</span>
    </div>
    <div class=${styles.weatherCond}>${t(conditionLabelKey(w.condition))}</div>
    ${w.city
      ? html`<div class=${styles.weatherCity} data-testid="weather-city">${w.city}</div>`
      : null}
    <div class=${styles.weatherRange}><span>↑&nbsp;${w.hi}°</span><span>↓&nbsp;${w.lo}°</span></div>
  </button>`;
}

// ── InfoStrip : 3 boxes (événement familial / repas / messages) ───────

function infoBoxFamilyEvent(events: readonly FamilyEvent[]): TemplateResult {
  const ev = nextFamilyEvent(events);
  return html`<button
    type="button"
    class=${styles.infoBox}
    data-testid="home-info-event"
    @click=${(): void => navigate('/planning')}
  >
    <span class=${styles.infoBoxKicker}>${t('home_box_family_event')}</span>
    ${ev
      ? html`<span class=${styles.infoBoxTitle}>${ev.title}</span>
          <span class=${styles.infoBoxMeta}
            >${shortDate(ev.date)}${ev.time ? ` · ${ev.time}` : ''}</span
          >`
      : html`<span class=${styles.infoBoxEmpty}>${t('home_no_family_event')}</span>`}
  </button>`;
}

function infoBoxMealsToday(meals: readonly Meal[]): TemplateResult {
  const { lunch, dinner } = todayMealsOf(meals);
  return html`<button
    type="button"
    class=${styles.infoBox}
    data-testid="home-info-meals"
    @click=${(): void => navigate('/meals')}
  >
    <span class=${styles.infoBoxKicker}>${t('home_box_meals_today')}</span>
    ${lunch || dinner
      ? html`${lunch
          ? html`<span class=${styles.infoBoxLine}
              ><strong>${t('home_meal_lunch')}</strong> — ${lunch}</span
            >`
          : null}
        ${dinner
          ? html`<span class=${styles.infoBoxLine}
              ><strong>${t('home_meal_dinner')}</strong> — ${dinner}</span
            >`
          : null}`
      : html`<span class=${styles.infoBoxEmpty}>${t('home_meals_to_plan')}</span>`}
  </button>`;
}

function infoBoxLastMessages(data: ClassicHomeData): TemplateResult {
  const msgs = lastMessagesOf(data.messages, 3);
  return html`<button
    type="button"
    class=${styles.infoBox}
    data-testid="home-info-messages"
    @click=${(): void => navigate('/messages')}
  >
    <span class=${styles.infoBoxKicker}>${t('home_box_last_messages')}</span>
    ${msgs.length === 0
      ? html`<span class=${styles.infoBoxEmpty}>${t('home_no_messages')}</span>`
      : msgs.map(
          (m) =>
            html`<span class=${styles.infoBoxLine}
              ><strong>${firstNameOf(data.members, m.who)}</strong> —
              ${m.text?.trim() || Icon(m.audioUrl ? Icons.mic : Icons.paperclip, 12)}</span
            >`,
        )}
  </button>`;
}

// ── Cartes membres : la journée de chacun (item 8) ────────────────────

function memberDayCard(homeId: string, data: ClassicHomeData, member: Member): TemplateResult {
  const events = eventsForMemberToday(data.events, member.id).sort((a, b) =>
    (a.time ?? '').localeCompare(b.time ?? ''),
  );
  const tasks = tasksForMemberOpen(data.tasks, member.id);
  const isEmpty = events.length === 0 && tasks.length === 0;

  return html`<div
    class=${styles.memberCard}
    data-testid=${`home-member-card-${member.id}`}
    data-member-id=${member.id}
  >
    <div class=${styles.memberCardHead}>
      <span class=${styles.memberAvatar} style=${`background:${avatarColorFor(member.id)}`}
        >${initialsOf(member.name)}</span
      >
      <span class=${styles.memberCardName}>${member.name.split(/\s+/)[0]}</span>
    </div>
    ${isEmpty
      ? html`<div class=${styles.memberEmpty}>${t('nothing_planned')}</div>`
      : html`<ul class=${styles.memberItems}>
          ${events.map(
            (e) =>
              html`<li class=${styles.memberItem}>
                <span class=${styles.memberItemTime}>${e.time ?? '·'}</span>
                <span class=${styles.memberItemTitle}>${e.title}</span>
              </li>`,
          )}
          ${tasks.map(
            (tk) =>
              html`<li class=${styles.memberItem}>
                <input
                  type="checkbox"
                  class=${styles.memberTaskCheck}
                  .checked=${tk.done}
                  aria-label=${tk.text}
                  data-testid=${`home-member-task-${tk.id}`}
                  @change=${(): void => {
                    toggleTask(homeId, tk.id);
                  }}
                />
                <span class=${styles.memberItemTitle}>${tk.text}</span>
              </li>`,
          )}
        </ul>`}
  </div>`;
}

function membersGrid(homeId: string, data: ClassicHomeData): TemplateResult {
  if (data.members.length === 0) {
    return html`<div class=${styles.card} data-testid="home-members-empty">
      <div class=${styles.cardEmpty}>${t('home_no_members_hint')}</div>
    </div>`;
  }
  return html`<div class=${styles.memberGrid} data-testid="home-members-grid">
    ${data.members.map((m) => memberDayCard(homeId, data, m))}
  </div>`;
}

function weekMealsCard(meals: readonly WeekMealEntry[]): TemplateResult {
  if (meals.length === 0) {
    return html`<div class=${styles.card} data-testid="home-week-card">
      <div class=${styles.cardEmpty} data-testid="home-week-empty">${t('nothing_planned')}</div>
    </div>`;
  }
  return html`<div class=${styles.card} data-testid="home-week-card">
    ${meals.map((m, idx) =>
      listRow({
        time: m.dayLabel,
        title: m.title,
        ...(m.meta ? { meta: m.meta } : {}),
        rowTag: { label: t('tag_meal_label'), kind: 'event' },
        dotColor: 'var(--sage)',
        last: idx === meals.length - 1,
        onClick: () => navigate('/meals'),
        testid: `home-meal-row-${m.id}`,
      }),
    )}
  </div>`;
}

function emptyHomeTemplate(): TemplateResult {
  return html`<section data-testid="view-home" class=${styles.view}>
    <div class=${styles.card} style="margin: 32px 24px;">
      <div class=${styles.cardEmpty} data-testid="home-empty">${t('no_homes_yet')}</div>
    </div>
  </section>`;
}

// ── Vue principale ────────────────────────────────────────────────────

export function renderHomeView(): TemplateResult {
  const home = getCurrentHome();
  if (!home) return emptyHomeTemplate();

  // Garde défensive : reconcileRouteWithHome devrait empêcher d'arriver
  // ici en mode coparenting, mais on protège quand même.
  if (home.mode !== 'classic') {
    return html`<section data-testid="view-home" class=${styles.view}></section>`;
  }

  const data = home.data as ClassicHomeData;
  const accent = accentNameFor({ name: home.name, data });
  const wMeals = weekMeals(data).slice(0, 3);

  return html`
    <section data-testid="view-home" class=${styles.view}>
      ${editorialHero({
        badge: { mode: 'classic', label: home.name },
        dateLine: dateLineToday(),
        title: t('hello'),
        pivotComma: true,
        trailing: weatherWidget(),
        trailingAlign: 'center',
        ...(accent ? { accent } : {}),
      })}
      <div class=${styles.infoStrip} data-testid="home-info-strip">
        ${infoBoxFamilyEvent(data.events)} ${infoBoxMealsToday(data.meals)}
        ${infoBoxLastMessages(data)}
      </div>
      ${sectionHead({
        kicker: t('home_kpi_today'),
        sub: t('home_section_members_sub'),
        right: `${data.members.length.toString()} ${t('members').toLowerCase()}`,
        testid: 'home-section-today',
      })}
      ${membersGrid(home.id, data)}
      ${sectionHead({
        kicker: t('this_week'),
        sub: t('home_section_week_sub'),
        right: html`<button
          type="button"
          class=${styles.viewAllBtn}
          data-testid="home-view-all-meals"
          @click=${(): void => navigate('/meals')}
        >
          ${t('home_view_all')}
        </button>`,
        testid: 'home-section-week',
      })}
      ${weekMealsCard(wMeals)}
    </section>
  `;
}
